/**
 * Post-auth redirect helpers.
 *
 * A coach who taps "sign in" from a shared help-request link must land back on
 * that exact match page — same path, same query (?help=1, tokens, etc.).
 * Everything is sanitized to a same-origin relative path so a crafted
 * ?redirect= can never bounce a signed-in user to another site.
 */

/** Sanitize an arbitrary redirect value to a safe relative path, or null. */
export function sanitizeRedirect(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    const origin =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(decoded, origin);
    if (url.origin !== origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}

/** Read and sanitize `?redirect=` from a query string (defaults to current URL). */
export function readRedirectParam(search?: string): string | null {
  const source =
    search ?? (typeof window !== "undefined" ? window.location.search : "");
  return sanitizeRedirect(new URLSearchParams(source).get("redirect"));
}

/** Build the `/login?redirect=…` href for a page that wants the user back. */
export function buildLoginHref(pathname: string, search = ""): string {
  return `/login?redirect=${encodeURIComponent(`${pathname}${search}`)}`;
}
